import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import type { UseFormSetValue } from 'react-hook-form';
import type { ConfigFilter } from '../../_configs/types';
import { standardFilter } from '../../_flux/defaults';
import type { Facets } from '../../_types/Car';
import { useAllFacetsContext } from '../../context/all-facets';
import { useSettingsContext } from '../../context/settings';
import useLocales from '../../hooks/useLocales';
import type { FilterMap } from '../../modules/fahrzeugsuche/filter/maps/filterMap';
import type { FahrzeugsucheFilterForm } from '../../modules/fahrzeugsuche/types';
import checkUndefined from '../../utils/tools/checkUndefined';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import getStyleQuery from '../../utils/tools/getStyleQuery';
import Iconify from '../iconify/component';

type ChipFiltersProps = {
    canReset: boolean;
    facets: Facets;
    filterElements: FilterMap;
    filters: FahrzeugsucheFilterForm;
    onFilters: UseFormSetValue<FahrzeugsucheFilterForm>;
    onResetFilters: VoidFunction;
};

export default function ChipFilters({ canReset, facets, filterElements, filters, onFilters, onResetFilters }: ChipFiltersProps) {
    const allFacets = useAllFacetsContext();
    const { translate } = useLocales();
    const settings = useSettingsContext();

    return (
        <Stack alignItems="flex-start" direction="row" gap={1} sx={{ [getStyleQuery()]: { width: 1 } }} flexWrap="wrap">
            {(Object.keys(filters) as (keyof FahrzeugsucheFilterForm)[])
                ?.filter((filterKey) => {
                    const filterValue = filters[filterKey];
                    const filterElementKey = Object.keys(filterElements)?.find((key) => filterElements?.[key]?.filterKey === filterKey) as ConfigFilter;
                    const filterElement = filterElements?.[filterElementKey];

                    return (
                        !['ignoreFirma', 'ignoreStandort', 'nfzHersteller', 'NFZ', 'order', 'moreCars', 'pageSize', 'sort', 'StandortQuelle'].includes(filterKey) &&
                        !checkUndefinedNullOrEmpty(filterValue) &&
                        checkUndefined(settings?.fahrzeugsuche?.forcedFilter?.[filterElement?.filterKey])
                    );
                })
                ?.map((filterKey) => {
                    const filterValue = filters[filterKey];
                    const filterElementKey = Object.keys(filterElements)?.find((key) => filterElements?.[key]?.filterKey === filterKey) as ConfigFilter;
                    const filterElement = filterElements?.[filterElementKey];

                    if (
                        Array.isArray(filterValue) ||
                        (typeof filterValue === 'number' && filterValue > -1) ||
                        (typeof filterValue === 'boolean' && filterValue === true) ||
                        (typeof filterValue === 'string' && filterValue !== '-100')
                    ) {
                        return Array.isArray(filterValue) ? (
                            filterElement?.type === 'range' ? (
                                <Stack key={`Fahrzeugsuche_FilterChips_Block_${filterKey}`} direction="row" alignItems="center" gap={1}>
                                    {!checkUndefinedNullOrEmpty(filterValue?.[0]) ? (
                                        <Chip
                                            color="primary"
                                            size="small"
                                            label={
                                                !settings?.general?.mockup
                                                    ? `${translate('common:filter.from')} ${filterValue?.[0]}${!checkUndefinedNullOrEmpty(filterElement?.filterComponentProps?.einheit) ? ` ${filterElement?.filterComponentProps?.einheit}` : ''}`
                                                    : checkUndefinedNullOrEmpty(filterElementKey)
                                                        ? translate('common:filter.setFilter')
                                                        : `${translate(`common:filter.${filterElementKey}` as any)}: ${translate('common:filter.set')}`
                                            }
                                            onDelete={() => {
                                                const newFilter =
                                                    !checkUndefinedNullOrEmpty(settings?.fahrzeugsuche?.forcedDefaultFilter?.[filterKey]) && checkUndefinedNullOrEmpty(filterValue?.[1])
                                                        ? settings?.fahrzeugsuche?.forcedDefaultFilter?.[filterKey]
                                                        : filterValue?.[1] === null
                                                            ? undefined
                                                            : [null, filterValue?.[1]];
                                                onFilters(filterKey as any, newFilter);
                                            }}
                                        />
                                    ) : undefined}
                                    {!checkUndefinedNullOrEmpty(filterValue?.[1]) ? (
                                        <Chip
                                            color="primary"
                                            size="small"
                                            label={
                                                !settings?.general?.mockup
                                                    ? `${translate('common:filter.to')} ${filterValue?.[1]}${!checkUndefinedNullOrEmpty(filterElement?.filterComponentProps?.einheit) ? ` ${filterElement?.filterComponentProps?.einheit}` : ''}`
                                                    : checkUndefinedNullOrEmpty(filterElementKey)
                                                        ? translate('common:filter.setFilter')
                                                        : `${translate(`common:filter.${filterElementKey}` as any)}: ${translate('common:filter.set')}`
                                            }
                                            onDelete={() => {
                                                const newFilter =
                                                    !checkUndefinedNullOrEmpty(settings?.fahrzeugsuche?.forcedDefaultFilter?.[filterKey]) && checkUndefinedNullOrEmpty(filterValue?.[0])
                                                        ? settings?.fahrzeugsuche?.forcedDefaultFilter?.[filterKey]
                                                        : filterValue?.[0] === null
                                                            ? undefined
                                                            : [filterValue?.[0], null];
                                                onFilters(filterKey as any, newFilter);
                                            }}
                                        />
                                    ) : undefined}
                                </Stack>
                            ) : (
                                filterValue?.map((f) => (
                                    <Chip
                                        key={`Fahrzeugsuche_FilterChips_Block_${filterKey}_Chip_${f}`}
                                        color="primary"
                                        size="small"
                                        label={
                                            !settings?.general?.mockup
                                                ? `${typeof f === 'boolean' ? translate(`common:filter.${filterElementKey}` as any) : allFacets?.[filterElement?.facetKey]?.find((a) => a?.i === f)?.t || '-'}`
                                                : checkUndefinedNullOrEmpty(filterElementKey)
                                                    ? translate('common:filter.setFilter')
                                                    : `${translate(`common:filter.${filterElementKey}` as any)}: ${translate('common:filter.set')}`
                                        }
                                        onDelete={() => {
                                            const newFilter = filterValue?.filter((val) => val !== f);
                                            onFilters(
                                                filterKey as any,
                                                !checkUndefinedNullOrEmpty(settings?.fahrzeugsuche?.forcedDefaultFilter?.[filterKey]) && checkUndefinedNullOrEmpty(newFilter)
                                                    ? settings?.fahrzeugsuche?.forcedDefaultFilter?.[filterKey]
                                                    : newFilter
                                            );
                                        }}
                                    />
                                ))
                            )
                        ) : (
                            <Chip
                                key={`Fahrzeugsuche_FilterChips_Block_${filterKey}`}
                                color="primary"
                                label={
                                    !settings?.general?.mockup
                                        ? filterElementKey === 'sofort' && !checkUndefinedNullOrEmpty(settings?.fahrzeugsuche?.customNameSofort)
                                            ? `${settings?.fahrzeugsuche?.customNameSofort}`
                                            : (filterElementKey === 'ezVon' || filterElementKey === 'kmVon' || filterElementKey === 'leistungVon' || filterElementKey === 'preisVon' || filterElementKey === 'rateVon')
                                                ? `${translate('common:filter.from')} ${filterValue}`
                                                : (filterElementKey === 'ezBis' || filterElementKey === 'kmBis' || filterElementKey === 'leistungBis' || filterElementKey === 'preisBis' || filterElementKey === 'rateBis')
                                                    ? `${translate('common:filter.to')} ${filterValue}`
                                                    : `${typeof filterValue === 'boolean'
                                                        ? translate(`common:filter.${filterElementKey}` as any)
                                                        : filterElement?.type === 'text' ? filterValue : facets?.[filterElement?.facetKey]?.find((a) => a?.i === filterValue)?.t}`
                                        : settings?.general?.mockup && checkUndefinedNullOrEmpty(filterElementKey)
                                            ? translate('common:filter.setFilter')
                                            : `${translate(`common:filter.${filterElementKey}` as any)}: ${translate('common:filter.set')}`
                                }
                                onDelete={() => onFilters(filterKey as any, settings?.fahrzeugsuche?.forcedDefaultFilter?.[filterKey] || standardFilter[filterKey])}
                                size="small"
                                sx={{ [getStyleQuery()]: { fontSize: (theme) => theme?.rawTypography?.body1?.fontSize } }}
                            />
                        );
                    }
                })}

            {canReset && (
                <Button color="error" onClick={onResetFilters} startIcon={<Iconify icon="solar:trash-bin-trash-bold" />} sx={{ flexShrink: 0, my: -0.75, transform: 'translateY(-8px)' }}>
                    {translate('common:buttons.clear')}
                </Button>
            )}
        </Stack>
    );
}
